/**
 * SummaryStatusIndicator Component
 * Shows status messages during summary generation
 * Handles: queued, processing, failed, and generating states
 */

import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SummaryStatusIndicatorProps } from './types';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from "../../lib/fonts";
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

export default function SummaryStatusIndicator({
    status,
    job,
    onRetry
}: SummaryStatusIndicatorProps) {
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const { fontsLoaded } = useFontLoading();

    if (status === 'queued') {
        const isRetry = job && job.attempt_count > 0;
        const attemptText = isRetry
            ? i18n._({ ...msg({ id: 'summary.attemptProgress', message: '(Attempt {attempt} of {maximum})' }), values: { attempt: job.attempt_count + 1, maximum: job.max_attempts } })
            : '';

        return (
            <View style={styles.container}>
                <View style={styles.iconContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                </View>
                <Text style={[styles.title, fontsLoaded && interSemiBold]}>
                    {isRetry ? <Trans id="papers.summarystatusindicator.retrying" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Retrying...</Trans> : <Trans id="papers.summarystatusindicator.waitingInQueue" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Waiting in queue...</Trans>} {attemptText}
                </Text>
                <Text style={[styles.message, fontsLoaded && interRegular]}>
                    {isRetry
                        ? <Trans id="papers.summarystatusindicator.retryingAfterPreviousAttemptThisUsuallyTakesJustAFew" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Retrying after previous attempt. This usually takes just a few moments.</Trans>
                        : <Trans id="papers.summarystatusindicator.yourSummaryRequestHasBeenQueuedThisUsuallyTakesJust" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Your summary request has been queued. This usually takes just a few moments.</Trans>}
                </Text>
                <Text style={[styles.emailNotice, fontsLoaded && interRegular]}><Trans id="papers.summarystatusindicator.youCanSafelyCloseThisScreenWeLlSendYouAn" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                    You can safely close this screen. We'll send you an email when your summary is ready.
                </Trans></Text>
            </View>
        );
    }

    if (status === 'processing') {
        const isRetry = job && job.attempt_count > 0;
        const attemptText = isRetry
            ? i18n._({ ...msg({ id: 'summary.attemptProgress', message: '(Attempt {attempt} of {maximum})' }), values: { attempt: job.attempt_count + 1, maximum: job.max_attempts } })
            : '';

        return (
            <View style={styles.container}>
                <View style={styles.iconContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                </View>
                <Text style={[styles.title, fontsLoaded && interSemiBold]}>
                    {isRetry ? <Trans id="papers.summarystatusindicator.retryingSummaryGeneration" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Retrying summary generation...</Trans> : <Trans id="papers.summarystatusindicator.generatingSummary" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Generating summary...</Trans>} {attemptText}
                </Text>
                <Text style={[styles.message, fontsLoaded && interRegular]}>
                    {isRetry
                        ? <Trans id="papers.summarystatusindicator.retryingAfterPreviousErrorOurAiIsAnalyzingThePaper" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Retrying after previous error. Our AI is analyzing the paper and creating a comprehensive summary. This typically takes 1-5 minutes.</Trans>
                        : <Trans id="papers.summarystatusindicator.ourAiIsAnalyzingThePaperAndCreatingAComprehensive" comment="Conditional UI copy in components/papers/SummaryStatusIndicator.tsx">Our AI is analyzing the paper and creating a comprehensive summary. This typically takes 1-5 minutes.</Trans>}
                </Text>
                <Text style={[styles.emailNotice, fontsLoaded && interRegular]}><Trans id="papers.summarystatusindicator.youCanSafelyCloseThisScreenWeLlSendYouAn" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                    You can safely close this screen. We'll send you an email when your summary is ready.
                </Trans></Text>
                {job?.started_at && (
                    <Text style={[styles.metadata, fontsLoaded && interRegular]}><Trans id="papers.summarystatusindicator.started" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                        Started: {new Date(job.started_at).toLocaleTimeString(formatLocale)}
                    </Trans></Text>
                )}
            </View>
        );
    }

    if (status === 'failed') {
        const errorMessage = i18n._(msg({ id: 'summary.generationFailure', message: 'Unable to generate summary for this paper' }));
        const attemptInfo = job
            ? i18n._({ ...msg({ id: 'summary.failedAttempts', message: 'Failed after {count, plural, one {# attempt} other {# attempts}}' }), values: { count: job.attempt_count } })
            : i18n._(msg({ id: 'summary.failed', message: 'Failed' }));

        return (
            <View style={[styles.container, styles.errorContainer]}>
                <View style={styles.iconContainer}>
                    <MaterialIcons name="error-outline" size={48} color={colors.errorRed} />
                </View>
                <Text style={[styles.title, styles.errorTitle, fontsLoaded && interSemiBold]}>
                    {attemptInfo}
                </Text>
                <Text style={[styles.message, fontsLoaded && interRegular]}>
                    {errorMessage}
                </Text>
                {job?.attempt_count && job?.max_attempts && (
                    <Text style={[styles.metadata, styles.errorMetadata, fontsLoaded && interRegular]}><Trans id="papers.summarystatusindicator.attemptedOfTimes" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                        Attempted {job.attempt_count} of {job.max_attempts} times
                    </Trans></Text>
                )}
                {onRetry && (
                    <TouchableOpacity
                        style={styles.retryButton}
                        onPress={onRetry}
                        activeOpacity={0.7}
                    >
                        <MaterialIcons name="refresh" size={20} color="white" style={styles.retryIcon} />
                        <Text style={[styles.retryButtonText, fontsLoaded && interSemiBold]}><Trans id="papers.summarystatusindicator.tryAgain" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                            Try Again
                        </Trans></Text>
                    </TouchableOpacity>
                )}
            </View>
        );
    }

    if (status === 'generating') {
        return (
            <View style={styles.container}>
                <View style={styles.iconContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                </View>
                <Text style={[styles.title, fontsLoaded && interSemiBold]}><Trans id="papers.summarystatusindicator.starting" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                    Starting...
                </Trans></Text>
                <Text style={[styles.message, fontsLoaded && interRegular]}><Trans id="papers.summarystatusindicator.preparingYourSummaryRequest" comment="UI copy in components/papers/SummaryStatusIndicator.tsx">
                    Preparing your summary request...
                </Trans></Text>
            </View>
        );
    }

    return null;
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 24,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: 'center',
    },
    errorContainer: {
        borderColor: colors.errorRed,
        backgroundColor: '#fef2f2',
    },
    iconContainer: {
        marginBottom: 16,
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 8,
        textAlign: 'center',
    },
    errorTitle: {
        color: colors.errorRed,
    },
    message: {
        fontSize: 15,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 8,
    },
    emailNotice: {
        fontSize: 14,
        color: colors.text500,
        textAlign: 'center',
        lineHeight: 20,
        marginBottom: 16,
    },
    metadata: {
        fontSize: 13,
        color: colors.text500,
        textAlign: 'center',
        marginTop: 8,
    },
    errorMetadata: {
        color: colors.errorRed,
        fontWeight: '500',
        marginBottom: 8,
    },
    retryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.primaryGreen,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 8,
        marginTop: 8,
    },
    retryIcon: {
        marginRight: 8,
    },
    retryButtonText: {
        color: 'white',
        fontSize: 16,
        fontWeight: '600',
    },
});
